import { PaginationState } from "@tanstack/react-table";
import { FaExclamationCircle } from "react-icons/fa";
import { format, isValid, parseISO } from "date-fns";
import { Field, Form, Formik, FormikErrors } from "formik";
import { nanoid } from "nanoid";
import Cookies from "js-cookie";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useBlocker, useNavigate, useParams, useSearchParams } from "react-router-dom";
import * as Yup from "yup";
import { setBlocker, clearBlocker } from '../../shared/slice/blockerSlice'
import {
    createGasBalloonOrder,
    downloadGasBalloonOrderFile,
    editGasBalloonOrder,
    getGasBalloonOrderXml,
    searchGasBalloonOrderById,
    signGasBalloonOrder,
} from "../../common/api/gasBalloonOrdersApi";
import { EmptyListPlaceholderContent } from "../../shared/Ui/EmptyPageBlock";
import SelectCustomFormik from "../../shared/Ui/SelectCustom/SelectCustomFormik";
import StatusBadge from "../../shared/Ui/StatusBadge/StatusBadge";
import Pagination from "../../shared/components/Table/Pagination";
import style from "./GasCilinders.module.css";
// @ts-ignore
import { NCALayerClient } from "ncalayer-js-client";
import AlertBox from "../../shared/Ui/AlertBox/AlertBox";
import ModalButton from "../../shared/Ui/Buttons/ModalButtons";
import Loader from "../../shared/Ui/Loader/Loader";
import ModalComponent from "../../shared/Ui/ModalComponent/ModalWindow";
import { useAppDispatch, useAppSelector } from "../../shared/hook";
import { useTranslation } from "react-i18next";
import { Tooltip } from "react-tooltip";
import PageNotRole from "../../shared/Ui/PageNotFound/PageNotRole";
import { getStatusTypeForOrders } from "../../shared/documentUtils/dictionary";
import { product_groups } from "../../shared/reference/product_groups";
import CheckBox from "../../shared/Ui/CheckBox/CheckBox";
import OrderUpload from "../Documents/Upload/OrderUpload/OrderUpload";

function NewGasCilinderOrders() {
    const roleMatrix = useAppSelector(state => state.roleMatrix)
    const [role, setRole] = useState<any>(null);
    const dispatch = useAppDispatch();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const hasOpenedFromQueryRef = useRef(false);
    const [releaseMethod, setReleaseMethod] = useState([]);
    const [isKazakhstanSelected, setIsKazakhstanSelected] = useState<any>(false);
    const [countryOrigin, setCountryOrigin] = useState([]);
    const { orderId } = useParams();
    const [alertOpen, setAlertOpen] = useState<any>(null);
    const [isFileUploadModalOpen, setIsFileUploadModalOpen] = useState<boolean>(false);
    const [toggleOnLoad, setToggleOnLoad] = useState<boolean>(false);
    const [createdOrderId, setCreatedOrderId] = useState<any>(null);
    const [stackDelete, setStackDelete] = useState<any>([]);
    const [stackEdit, setStackEdit] = useState<any>([]);
    const [stackAddCount, setStackAddCount] = useState<any>(0);
    const [isFormChanged, setIsFormChanged] = useState(false);
    const [importedFromFile, setImportedFromFile] = useState(false);
    const activeGroupId = Number(Cookies.get("activeGroup") ?? "");
    const currentGroup = product_groups.find((pg) => pg.id === activeGroupId);
    const saveButtonRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [statusType, setStatusType] = useState({
        type: "unsaved",
        label: t("documentStatus.new"),
    });
    const [productTypeCode, setProductTypeCode] = useState<any[]>([]);
    const [errorModal, setErrorModal] = useState<any>(null);
    const [dowloadModal, setDowloadModal] = useState<any>(null);
    const pageSize = useAppSelector((state) => state.userSettings.pageSize);
    const [isLoading, setIsLoading] = useState(false);
    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: pageSize,
    });
    interface ITableDataItem {
        orderId: number;
        productTypeCode: string;
        amount: string;
        weight: string;
        size: string;
    }

    interface IInitialValues {
        id: string;
        org_id: string;
        create_user_id: string;
        createdAt: any;
        updatedAt: any;
        signingAt: any;
        status: string;
        refReleaseMethod: string | undefined;
        refCountryOrigin: any;
        note: any;
        error_reason: string;
        number: string;
        quantity: string;
        importedFromFile?: boolean;
        productTypeCode?: string | null;
        downloadAvailable?: boolean;
        orderProducts: ITableDataItem[];
    }
    const [initialValues, setInitialValues] = useState<IInitialValues>({
        id: "",
        org_id: "generatedOrgId", // ID организации
        create_user_id: "generatedUserId", // ID создавшего пользователя
        createdAt: "", // Дата и время создания
        updatedAt: "timestamp", // Дата и время последнего изменения
        signingAt: "timestamp", // Дата и время подписания
        status: "new", // Статус, например "Обработан", "Ошибочный" и т.д.
        refReleaseMethod: undefined, // Способ выпуска в оборот, ID метода
        refCountryOrigin: undefined, // Страна производства, ID страны
        note: null, // Примечание
        error_reason: "", // Причина ошибки
        number: "", // Регистрационный номер
        quantity: "",
        importedFromFile: false,
        productTypeCode: null,
        downloadAvailable: false,
        orderProducts: [],
    });



    const formValidationSchema = useMemo(() => {
        return Yup.object().shape({
            note: Yup.string().nullable()
                .min(3, t("documentsError.minCharacters", { count: 3 }))
                .max(256, t("documentsError.maxCharacters", { count: 256 })),
            quantity: Yup.number()
                .typeError(t("documentsError.required"))
                .required(t("documentsError.required"))
                .integer("Количество должно быть целым числом")
                .min(1, "Минимальное количество — 1")
                .max(30000, "Максимальное количество — 30 000"),
        });
    }, [t]);

    function onLoad() {
        if (!orderId) return;
        setLoading(true);
        searchGasBalloonOrderById(orderId)
            .then((orderResponse) => {
                const data = orderResponse?.data ? orderResponse.data : {};
                setInitialValues({
                    ...initialValues,
                    ...data,
                    quantity: data?.codesAmount ?? "",
                    note: data?.note ?? null,
                    orderProducts: [],
                });
                setLoading(false);
            })
            .catch((error) => {
                navigate("/error", {
                    state: { error: error?.response?.data?.message?.toString() },
                });
                setLoading(false);
            });
    }

    useEffect(() => {
        const roleObj = () => { return roleMatrix?.roleMatrix?.find(item => item.id === 2) }
        setRole(roleObj)
        if (orderId !== undefined) {
            onLoad();
        } else {
            setLoading(false);
        }
    }, [orderId]);

    // Обновляем данные заказа (в т.ч. orderProducts) после успешной загрузки файла
    useEffect(() => {
        if (orderId) {
            onLoad();
        }
    }, [toggleOnLoad]);

    const roleAcces = (actionId: string) => {
        return roleMatrix?.roleMatrix
            ?.find((item: any) => item.id === 2)
            ?.specificAction?.actions
            ?.some((action: any) => action.id === actionId && action.active) || false;
    }

    useEffect(() => {
        const { type, label } = getStatusTypeForOrders(initialValues.status, t);
        setStatusType({ type, label });
    }, [initialValues]);

    

    function markOrderFormTouched(formik: any) {
        formik.setTouched(
            {
                note: true,
                quantity: true,
            },
            true,
        );
    }

    function showOrderFormValidationErrors(formik: any) {
        markOrderFormTouched(formik);
        setAlertOpen({
            status: "error",
            message: t("new_order.form_validation_error"),
        });
    }

    async function handleSaveOrder(
        formik: any,
        options?: { openUploadQuery?: boolean },
    ) {
        const errors = await formik.validateForm();
        if (Object.keys(errors).length > 0) {
            showOrderFormValidationErrors(formik);
            return;
        }
        await submitCreate(formik.values, options);
    }

    async function submitCreate(formikValues: any, options?: { openUploadQuery?: boolean }) {
        dispatch(clearBlocker())
        setIsFormChanged(false);
        const backendData = {
            codesAmount: Number(formikValues.quantity),
            note: formikValues.note || null,
        };

        if (orderId !== undefined) {
            await editGasBalloonOrder(orderId, backendData)
                .then((response) => {
                    dispatch(clearBlocker())
                    setIsFormChanged(false);
                    onLoad();
                    setAlertOpen({
                        status: response.status,
                        message: response.message
                            ? response.message
                            : t("new_order.save_success_message"),
                    });
                })
                .catch((error) => {
                    setAlertOpen({
                        status: error,
                        message: t("new_order.file_error_message"),
                    });

                });
        } else {
            await createGasBalloonOrder(backendData)
                .then((response) => {
                    try {
                        const newId = response?.data?.id ?? response?.id;
                        if (newId) {
                            setCreatedOrderId(newId);
                        }
                    } catch { }
                    navigate(`/gasCilindersOrders/${response.data.id}`)
                })
                .catch((error) => {
                    console.error("Error creating new GB order:", error);
                    const e = error as { response?: { data?: { message?: string } } };
                    const errorMessage =
                        e.response?.data?.message || t("Произошла ошибка при создании заказа");
                    setErrorModal({ errorMessage });
                    setAlertOpen({
                        status: error,
                        message: t("new_order.create_error_message"),
                    });
                });
        }
    }

    // Это ЗАПРОС НА ПОДДПМСАНИЕ ЗАЯВКИ!
    async function submitXmlProduct() {
        const ncalayerClient = new NCALayerClient();
        try {
            await ncalayerClient.connect();
        } catch (error) {
            setAlertOpen({
                status: "error",
                message: t("documentNotification.nca_no_connect"),
            });
            return;
        }

        if (!orderId) {
            return;
        }

        let xml;
        try {
            setIsLoading(true);
            xml = await getGasBalloonOrderXml(orderId);
        } catch (error) {
            const e = error as { response?: { data?: { message?: string } } }; // Утверждаем тип
            const errorMessage =
                e.response?.data?.message || t("new_order.get_xml_error");
            setErrorModal({ errorMessage });
            return;
        } finally {
            setIsLoading(false);
        }

        let xmlSignature;
        try {
            xmlSignature = await ncalayerClient.basicsSignXML(
                NCALayerClient.basicsStoragesAll,
                xml.data,
                NCALayerClient.basicsXMLParams,
                process.env.REACT_APP_ETRACK_TEST === "true"
                    ? NCALayerClient.basicsSignerTestAny
                    : NCALayerClient.basicsSignerSignAny,
                "ru",
            );
        } catch (error) {
            const e = error as { response?: { data?: { message?: string } } }; // Утверждаем тип
            const errorMessage =
                e.response?.data?.message || t("new_order.sign_xml_error");
            setErrorModal({ errorMessage });
            return;
        }

        try {
            setIsLoading(true);
            const orderIdValue = orderId ?? "0";
            await signGasBalloonOrder(orderIdValue, xmlSignature[0]);
            alert(t("new_order.edit_success_message"));
            navigate("/gasCilindersOrders");
        } catch (error) {
            const e = error as { response?: { data?: { message?: string } } }; // Утверждаем тип
            const errorMessage =
                e.response?.data?.message || t("new_order.save_error_message");
            setErrorModal({ errorMessage });
            onLoad();
        } finally {
            setIsLoading(false);
        }
    }

    // Это ЗАПРОС НА ФАЙЫ ЗАЯВКИ!
    const handleDownloadClick = async (orderId: any, fileType: "PDF" | "XLSX") => {
        try {
            setIsLoading(true);
            const fileBlob = await downloadGasBalloonOrderFile(orderId, fileType);
            const downloadUrl = window.URL.createObjectURL(fileBlob);
            const link = document.createElement("a");
            link.href = downloadUrl;
            link.setAttribute("download", `order-${orderId}.${fileType}`);
            document.body.appendChild(link);
            link.click();
            if (link.parentNode) {
                link.parentNode.removeChild(link);
            }
        } catch (error) {
            console.error("Ошибка при скачивании файла:", error);

            // Утверждаем тип ошибки
            const e = error as {
                response?: {
                    status?: number;
                    data?: { message?: string };
                };
            };

            // Проверяем HTTP-код и тело ошибки
            if (e.response?.status === 403) {
                const errorMessage = e.response.data?.message ||
                    t("На лицевом счете недостаточно средств, его необходимо пополнить для печати кодов маркировки.");
                setErrorModal({ errorMessage });
            } else {
                const errorMessage = t("Не удалось отправить запрос во внешнюю систему. Попробуйте, пожалуйста, позднее или обратитесь в службу поддержки");
                setErrorModal({ errorMessage });
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Это БОКИРУЕМ ПОКИДАНИЕ СТРАНИЦЫ ЕСЛИ ЕСТЬ ИЗМЕНЕНИЯ!
    useEffect(() => {
        const handleBeforeUnload = (event: any) => {
            // Проверяем состояние кнопки "Сохранить"
            if (isFormChanged) {
                event.preventDefault();
                event.returnValue = t("documentNotification.unsaved_changes_warning");
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [isFormChanged]);

    const { isBlocked, proceed } = useAppSelector((state) => state.blocker);
    let blocker = useBlocker(
        ({ currentLocation, nextLocation }) =>
            isBlocked !== false &&
            currentLocation.pathname !== nextLocation.pathname,
    );

    function isWithinTenDays(signingAt: string): boolean {
        const createdDate = new Date(signingAt.split(' ')[0].split('.').reverse().join('-') + 'T' + signingAt.split(' ')[1]);
        const currentDate = new Date();
        const differenceInMilliseconds = currentDate.getTime() - createdDate.getTime();

        const differenceInDays = differenceInMilliseconds / (1000 * 60 * 60 * 24);

        return differenceInDays <= 10;
    }


    if (loading && !role) return <Loader />;

    if (!role || !role?.view) {
        return (
            <PageNotRole />
        )
    }

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={(formikValues) => submitCreate(formikValues)}
            enableReinitialize={true}
            validateOnBlur={true}
            validateOnChange={true}
            validationSchema={formValidationSchema}
        >
            {(formik) => {
                const totalAmount = formik.values.orderProducts.reduce(
                    (acc, current) => acc + Number(current.amount || 0),
                    0,
                );

                const kmTypeOptions = [
                    { value: "CONSUMER", label: t("new_order.km_type_consumer") },
                ];
                if (activeGroupId !== 3) {
                    kmTypeOptions.push(
                        { value: "GROUP", label: t("new_order.km_type_group") },
                    );
                }

                return (
                    <Form className="max-w-[1585px] w-full m-t-[20px]">
                        {isBlocked && blocker.state === "blocked" ? (
                            <ModalComponent
                                isOpen={true} // Модальное окно всегда открыто, когда blocker.state === "blocked"
                                onClose={() => {
                                    if (blocker && typeof blocker.reset === "function") {
                                        blocker.reset();
                                    } else {
                                        console.error("Reset function is not available.");
                                    }
                                }}
                                headerContent={
                                    <>
                                        <span>{t("documentNotification.notification")}</span>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (blocker && typeof blocker.reset === "function") {
                                                    blocker.reset();
                                                } else {
                                                    console.error("Reset function is not available.");
                                                }
                                            }}
                                            aria-label="Close"
                                            className="float-right cursor-pointer bg-none border-none"
                                        >
                                            &times;
                                        </button>
                                    </>
                                }
                                content={<p>{t("documentNotification.unsaved_changes_warning")}</p>}
                                customContent={
                                    <>
                                        <ModalButton
                                            type="button"
                                            onClick={() => {
                                                dispatch(clearBlocker())
                                                if (blocker && typeof blocker.proceed === "function") {
                                                    blocker.proceed(); // Уходим со страницы без сохранения
                                                } else {
                                                    console.error("Proceed function is not available.");
                                                }
                                            }}
                                        >
                                            {t("mainButtons.cancel")}
                                        </ModalButton>
                                        <ModalButton
                                            type="button"
                                            color="green"
                                            onClick={async () => {
                                                dispatch(clearBlocker())
                                                try {
                                                    await handleSaveOrder(formik); // Сохранение данных
                                                    if (blocker && typeof blocker.proceed === "function") {
                                                        blocker.proceed(); // Переход после сохранения
                                                    }
                                                } catch (error) {
                                                    console.error("Error saving data:", error);
                                                }
                                            }}
                                        >
                                            {t("mainButtons.save")}
                                        </ModalButton>
                                    </>
                                }
                            />
                        ) : null}
                        <ModalComponent
                            isOpen={errorModal}
                            onClose={() => {
                                setErrorModal(null);
                            }}
                            headerContent={t("documentNotification.notification")}
                            content={
                                <p>
                                    {" "}
                                    {`${t("documents_file_upload.errors_list")}: ${errorModal?.errorMessage ? errorModal?.errorMessage : "ошибка."}`}
                                </p>
                            }
                            customContent={
                                <ModalButton
                                    type="button"
                                    onClick={() => {
                                        setErrorModal(null);
                                        // navigate(`/orders`)
                                    }}
                                >
                                    {t("mainButtons.accept")}
                                </ModalButton>
                            }
                        />
                        {/* ТУТ СКАЧИВАЮТСЯ ДОКУМЕНТЫ */}
                        <ModalComponent
                            isOpen={dowloadModal}
                            onClose={() => {
                                setDowloadModal(null);
                            }}
                            headerContent={t("orderList2.downloadFile")}
                            content={
                                <div className="flex flex-col space-y-2">
                                    <button
                                        onClick={() => {
                                            handleDownloadClick(orderId, "PDF")
                                            setDowloadModal(null)
                                        }}
                                        className="bg-red-500 text-white font-semibold py-2 px-4 rounded hover:bg-blue-700 transition duration-300"
                                        type="button"
                                    >
                                        {t("orderList.download")} PDF
                                    </button>
                                    <button
                                        onClick={() => {
                                            handleDownloadClick(orderId, "XLSX")
                                            setDowloadModal(null)
                                        }}
                                        className="bg-blue-500 text-white font-semibold py-2 px-4 rounded hover:bg-purple-700 transition duration-300"
                                        type="button"
                                    >
                                        {t("orderList.download")} XLSX
                                    </button>
                                </div>
                            }
                            customContent={
                                <ModalButton
                                    type="button"
                                    color="grey"
                                    onClick={() => {
                                        setDowloadModal(null);
                                    }}
                                >
                                    {t("mainButtons.close")}
                                </ModalButton>
                            }
                        />
                        <div className="flex flex-col  ">
                            <div className="flex items-center flex-row text-[20px] font-bold gap-[6px] text-gray-700">
                                <div className="flex flex-col">
                                    <StatusBadge type={statusType.type}>
                                        {`${t("documentLabels.status")} : ` + statusType.label}
                                    </StatusBadge>
                                    <div className="text-[14px] font-semibold text-gray-700 ml-[9px]">
                                        {initialValues && (
                                            (() => {
                                                const dateStr =
                                                    initialValues?.updatedAt ||
                                                    initialValues?.signingAt ||
                                                    initialValues?.createdAt;

                                                if (dateStr) {
                                                    const date = new Date(dateStr);

                                                    if (!isNaN(date.getTime())) {
                                                        return format(date, "dd.MM.yyyy, HH:mm");
                                                    }
                                                }

                                                return "";
                                            })()
                                        )}
                                    </div>
                                </div>
                                {initialValues.createdAt ? (
                                    <>
                                        <span>
                                            {`${t("new_order.order")} ` + formik.values.number}
                                        </span>
                                        <span></span>
                                    </>
                                ) : (
                                    <span>{t("Создание заказа кодов ГБ")}</span>
                                )}
                            </div>
                            <div className="flex flex-col bg-white  my-5 p-5  pt-8 relative rounded-xl shadow-md gap-[20px] overflow-visible">
                                <div className="flex flex-wrap justify-between gap-[20px] ">
                                    <div className="flex justify-center "></div>
                                    <div className="flex flex-row gap-[20px]">
                                        {/* Показываем кнопки только после завершения загрузки */}
                                        {!loading && (
                                            <>
                                                {/* Всегда показываем кнопку "Закрыть" */}
                                                <button
                                                    className="bg-bg hover:brightness-75 text-gray-500 font-bold py-2 px-4 rounded"
                                                    type="button"
                                                    onClick={() => navigate(`/gasCilindersOrders`)}
                                                >
                                                    {t("mainButtons.close")}
                                                </button>

                                                {/* Проверяем статус заказа, если не IN_PROCESSING, то отображаем другие кнопки */}
                                                {formik.values.status !== "IN_PROCESSING" && (
                                                    <>
                                                        {/* Статусы "new" и "DRAFT" для отображения кнопки "Сохранить" и "Отправить и Подписать" */}
                                                        {((formik.values.status === "new" && role.edit) ||
                                                            (formik.values.status == "DRAFT" && role.edit)) && (
                                                                <>
                                                                    {/* Кнопка "Сохранить" */}
                                                                    <button
                                                                        ref={saveButtonRef}
                                                                        id="saveButton"
                                                                        className="buttonEtrackc bg-green hover:brightness-75 text-white font-bold py-2 px-4 rounded"
                                                                        type="button"
                                                                        disabled={
                                                                            !formik.dirty ||
                                                                            !formik.values.quantity
                                                                        }
                                                                        onClick={async () => {
                                                                            setIsFormChanged(!formik.dirty || !formik.isValid);
                                                                            await handleSaveOrder(formik);
                                                                        }}
                                                                    >
                                                                        {t("mainButtons.save")}
                                                                    </button>

                                                                    {/* Кнопка "Отправить и Подписать" */}
                                                                    {roleAcces('SIGN_BY_EDS') && (
                                                                        <button
                                                                            className="buttonEtrackc bg-green hover:brightness-75 text-white font-bold py-2 px-4 rounded"
                                                                            type="button"
                                                                            onClick={() => submitXmlProduct()}
                                                                            disabled={!orderId || formik.dirty}
                                                                        >
                                                                            {t("mainButtons.sign")}
                                                                        </button>
                                                                    )}
                                                                </>
                                                            )}

                                                        {/* Если статус не "new", не "DRAFT", показываем кнопку "Скачать" */}
                                                        {
                                                            formik.values.downloadAvailable &&
                                                            formik.values.status !== "new" &&
                                                            roleAcces('PRINT_KM_LIST') && (
                                                                <button
                                                                    className="buttonEtrackc bg-green hover:brightness-75 text-white font-bold py-2 px-4 rounded"
                                                                    type="button"
                                                                    onClick={() => setDowloadModal(true)}
                                                                >
                                                                    {t("mainButtons.download")}
                                                                </button>
                                                            )}
                                                    </>
                                                )}
                                            </>
                                        )}

                                        <img src="/icons/update.svg"></img>
                                    </div>
                                </div>
                                <div className="flex gap-[20px] flex-wrap">
                                    <div className={style.inputField2}>
                                        <div className={style.labelAndButtonWrapper}>
                                            <label className={style.inputLabel}>
                                                {t("Количество кодов ГБ")}
                                            </label>
                                            {formik.touched.quantity && formik.errors.quantity && (
                                                <div className={style.tooltipWrapper}>
                                                    <FaExclamationCircle className={style.errorIcon} />
                                                    <div className={style.tooltipText}>
                                                        {typeof formik.errors.quantity === "string"
                                                            ? formik.errors.quantity
                                                            : t("documentsError.required")}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <Field
                                            type="number"
                                            name="quantity"
                                            placeholder={t("Введите количество")}
                                            className={style.placeholder}
                                            value={formik.values.quantity}
                                            disabled={
                                                (formik.values.status !== "new" &&
                                                    formik.values.status !== "DRAFT") ||
                                                !role?.edit
                                            }
                                            onChange={(e: any) => {
                                                formik.handleChange(e);
                                                dispatch(setBlocker(true))
                                                setIsFormChanged(true);
                                            }}
                                        />
                                        <div>
                                            <p
                                                className={"text-gray-500 font-[400] text-[14px]"}
                                            >
                                                {t("Целое число от 1 до 30 000 за один заказ")}
                                            </p>
                                        </div>
                                    </div>
                                    <div className={style.inputField1}>
                                        <div className={style.labelAndButtonWrapper}>
                                            <label className={style.inputLabel}>
                                                {t("new_order.note_label")}
                                            </label>
                                            {formik.touched.note && formik.errors.note && (
                                                <div className={style.tooltipWrapper}>
                                                    <FaExclamationCircle className={style.errorIcon} />
                                                    <div className={style.tooltipText}>
                                                        {typeof formik.errors.note === "string"
                                                            ? formik.errors.note
                                                            : t("new_order.country_origin_error")}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <Field
                                            type="text"
                                            name="note"
                                            placeholder={t("documentLabels.choose_option")}
                                            className={style.placeholder}
                                            value={formik.values.note}
                                            disabled={
                                                (formik.values.status !== "new" &&
                                                    formik.values.status !== "DRAFT") ||
                                                !role?.edit
                                            }
                                            maxLength={256}
                                            onChange={(e: any) => {
                                                formik.handleChange(e);
                                                dispatch(setBlocker(true))
                                                setIsFormChanged(true);
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>


                            {formik.values.orderProducts?.length > 0 && (
                                <Pagination
                                    totalItems={formik.values.orderProducts?.length}
                                    totalPages={Math.ceil(
                                        formik.values.orderProducts?.length / pagination.pageSize,
                                    )}
                                    pagination={pagination}
                                    setPagination={setPagination}
                                />
                            )}
                        </div>
                        {isLoading && <Loader />}
                        <AlertBox isOpen={alertOpen} setIsOpen={setAlertOpen} />
                        <OrderUpload
                            isOpen={isFileUploadModalOpen}
                            setIsOpen={setIsFileUploadModalOpen}
                            documentId={orderId}
                            toggleOnLoad={toggleOnLoad}
                            setToggleOnLoad={setToggleOnLoad}
                        />
                    </Form>
                );
            }}
        </Formik>
    );
}

export default NewGasCilinderOrders;
